import { HttpUploadProgressEvent } from '@angular/common/http';
import { signal, Signal } from '@angular/core';

export type ProgressValue = {
  percentage: number;
  timeRemaining?: string;
  secondsRemaining?: number;
  averageSpeed?: string;
};

export type ProgressSignal = Signal<ProgressValue> & {
  set: (event: ProgressEvent | HttpUploadProgressEvent) => void;
  reset: () => void;
};

//eslint-disable-next-line max-lines-per-function
export function progressSignal(): ProgressSignal {
  let startTime: number | null = null;
  const internalSignal = signal<ProgressValue>({
    percentage: 0,
  });

  const signalFn = internalSignal.asReadonly() as ProgressSignal;

  signalFn.set = (event: ProgressEvent | HttpUploadProgressEvent) => {
    if (!startTime) {
      startTime = Date.now();
    }
    const currentTime = Date.now();
    const elapsedSeconds = startTime ? (currentTime - startTime) / 1000 : 0;
    const loaded = event.loaded;
    const total = event.total ?? 0;

    let percentage = 0;
    let secondsRemaining = 0;
    let averageSpeedBytesPerSecond = 0;
    if (elapsedSeconds <= 0 || total === 0) {
      internalSignal.set({ percentage: 0 });
      return;
    }

    averageSpeedBytesPerSecond = loaded / elapsedSeconds;

    percentage = Math.round((loaded / total) * 100);
    if (averageSpeedBytesPerSecond > 0) {
      secondsRemaining = Math.round((total - loaded) / averageSpeedBytesPerSecond);
    }

    internalSignal.set({
      percentage: percentage,
      secondsRemaining: secondsRemaining,
      timeRemaining: formatSeconds(secondsRemaining),
      averageSpeed: formatBytes(averageSpeedBytesPerSecond) + '/s',
    });
    if (percentage >= 100) {
      startTime = null;
    }
  };
  signalFn.reset = () => {
    startTime = null;
    internalSignal.set({
      percentage: 0,
    });
  };

  return signalFn;
}

function formatSeconds(seconds: number): string {
  if (seconds <= 0 || !isFinite(seconds)) {
    return '00:00:00';
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return [h, m, s].map((v) => v.toString().padStart(2, '0')).join(':');
}

function formatBytes(bytes: number): string {
  if (bytes === 0 || !isFinite(bytes)) {
    return '0 B';
  }
  const k = 1024;
  const sizes = ['B', 'kB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}
